import type { QueryClient } from '@tanstack/react-query';
import type { Editor } from '@tiptap/core';
import type {
  SuggestionOptions,
  SuggestionProps as TTSuggestionProps,
} from '@tiptap/suggestion';

import { ReactRenderer } from '@tiptap/react';
import { forwardRef, useEffect, useState } from 'react';
import { TEAM_MEMBERS } from 'queries/query-keys';
import TeamMemberService from '__services/TeamMemberService';
import { getTeamLabel } from 'services/helpers';
import {
  type SuggestionListHandle,
  SuggestionList,
} from '../components/SuggestionList';
import { SuggestionPopover } from '../components/SuggestionPopover';
import { isComposingKeyboardEvent } from 'utility/keyboard';

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_ORDER = 'first_name';

type TeamMember = {
  displayName: string;
  email: string;
  firstName: string;
  id: string;
  lastName: string;
};

type TeamMemberResponse = {
  count: number;
  errors: any;
  next: null | string;
  previous: null | string;
  results: TeamMember[];
};

type TeamMemberOption = {
  id: string;
  label: string;
};

type FetchTeamMemberOptions = {
  editor: Editor;
  query: string;
};

type FetchTeamMembers = (
  options: FetchTeamMemberOptions
) => Promise<TeamMemberOption[] | AsyncIterable<TeamMemberOption>>;

interface TeamMemberListProps extends Omit<TTSuggestionProps, 'items'> {
  items: TeamMemberOption[] | AsyncIterable<TeamMemberOption>;
  portalTarget: Element;
  zIndex?: number;
}

export interface TeamMemberSuggestionOptions
  extends Omit<SuggestionOptions, 'items'> {
  items: (props: {
    query: string;
    editor: Editor;
  }) => Promise<TeamMemberOption[] | AsyncIterable<TeamMemberOption>>;
}

const isAsyncIterable = (
  items: any
): items is AsyncIterable<TeamMemberOption> => {
  return !Array.isArray(items) && Boolean(items[Symbol.asyncIterator]);
};

export const TeamMemberList = forwardRef<
  SuggestionListHandle,
  TeamMemberListProps
>((props, ref) => {
  const { command, decorationNode, items, portalTarget, zIndex } = props;
  const [iterableData, setIterableData] = useState<TeamMemberOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const isItemsAsyncIterable = isAsyncIterable(items);

  useEffect(() => {
    const getData = async () => {
      try {
        const data = [];
        setIsLoading(true);

        for await (const item of items) {
          data.push(item);
        }

        setIterableData(data);
      } finally {
        setIsLoading(false);
      }
    };

    if (isItemsAsyncIterable) {
      getData();
    }
  }, [isItemsAsyncIterable, items]);

  return (
    <SuggestionPopover
      width="350px"
      target={decorationNode}
      portalTarget={portalTarget}
      zIndex={zIndex}
    >
      <SuggestionList
        ref={ref}
        isLoading={isLoading}
        items={isItemsAsyncIterable ? iterableData : items}
        onSelect={(item: TeamMemberOption) => {
          command({ id: item.id, label: item.label, stamp: Date.now() });
        }}
      />
    </SuggestionPopover>
  );
});

/**
 * A debounced function that gets the items for the suggestion list. react-query is used
 * to make the api call via the queryClient. If the current query is present in the
 * cache, it will clear the current timeout and return the cached value. This cancels out
 * the function call until a new query value is provided that does not have a cache value.
 *
 * @remarks A result needs to be returned immediately so characters are not dropped.
 * An async iterable is returned when the query is empty to achieve this. The iterable
 * also allows {@link TeamMemberList} to create a loading state for the initial request.
 *
 * @param {*} queryClient - https://react-query.tanstack.com/reference/QueryClient
 * @param {number} debounceValue - number of milliseconds to debounce (default 300)
 * @returns async function where the first parameter is the query string. This function
 * corresponds to the @tiptap/suggestion `items` property (https://github.com/ueberdosis/tiptap/blob/main/packages/suggestion/src/suggestion.ts)
 */
const fetchTeamMembers = (
  queryClient: QueryClient,
  debounceValue = 300,
  pageSize = DEFAULT_PAGE_SIZE
): FetchTeamMembers => {
  let timeoutId: NodeJS.Timeout | null = null;

  return async ({ query }: FetchTeamMemberOptions) => {
    if (timeoutId) clearTimeout(timeoutId);

    const mapper = (tm: TeamMember): TeamMemberOption => {
      return { label: getTeamLabel(tm), id: tm.id };
    };

    const searchParams = {
      search: query.toLowerCase(),
      ordering: DEFAULT_ORDER,
      page_size: pageSize,
    };

    const queryKey = [
      TEAM_MEMBERS.TYPEAHEAD_LIST(false),
      pageSize,
      query.toLowerCase(),
    ];

    const cachedData =
      queryClient.getQueryData<TeamMemberResponse>(queryKey)?.results;
    if (cachedData) {
      return Promise.resolve(cachedData.map(mapper));
    }

    if (!query) {
      return {
        async *[Symbol.asyncIterator]() {
          const data = await queryClient.fetchQuery<TeamMemberResponse>({
            queryKey,
            queryFn: async () => {
              return TeamMemberService.getTeamMemberTypeahead(searchParams);
            },
          });
          if (Array.isArray(data?.results)) {
            for (const item of data.results) {
              yield mapper(item);
            }
          }
        },
      };
    }

    return new Promise((resolve) => {
      timeoutId = setTimeout(async () => {
        const data = await queryClient.fetchQuery<TeamMemberResponse>({
          queryKey,
          queryFn: () => TeamMemberService.getTeamMemberTypeahead(searchParams),
        });
        timeoutId = null;

        resolve((data?.results ?? []).map(mapper));
      }, debounceValue);
    });
  };
};

export const getTeamMemberSuggestionOptions = (
  queryClient: QueryClient,
  opts: {
    appendToSelector?: Element | string;
    props?: Record<string, any>;
    zIndex?: number;
  } = {}
): Omit<TeamMemberSuggestionOptions, 'editor'> => {
  return {
    items: fetchTeamMembers(queryClient),
    render() {
      let reactRenderer: ReactRenderer<SuggestionListHandle, any>;

      return {
        onStart: (props) => {
          const appendToNode =
            typeof opts.appendToSelector === 'string'
              ? props.decorationNode?.closest(opts.appendToSelector) ??
                document.querySelector(opts.appendToSelector)
              : opts.appendToSelector;
          const portalTarget = appendToNode || document.body;

          reactRenderer = new ReactRenderer(TeamMemberList, {
            props: {
              ...props,
              ...opts.props,
              portalTarget,
              zIndex: opts?.zIndex,
            },
            editor: props.editor,
          });
        },
        onUpdate(props) {
          reactRenderer?.updateProps(props);
        },
        onKeyDown(props) {
          const { event } = props;

          if (isComposingKeyboardEvent(event)) {
            return false;
          }

          if (!reactRenderer) {
            return false;
          }

          if (event.key === 'Escape') {
            reactRenderer.destroy();

            return true;
          }

          const items = reactRenderer.props.items;

          if (
            !isAsyncIterable(items) &&
            !items.length &&
            (event.key === 'Enter' || event.key === 'Tab')
          ) {
            reactRenderer.destroy();

            return true;
          }

          return reactRenderer.ref?.onKeyDown(props) ?? false;
        },
        onExit() {
          reactRenderer?.destroy();
        },
      };
    },
  };
};
